import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BrandMark } from "@/components/Brand";
import ToolbarToggles from "@/components/ToolbarToggles";
import { toast } from "sonner";
import { Key, EnvelopeSimple, ArrowLeft, Copy } from "@phosphor-icons/react";

// Only show the demo credentials block in non-production environments.
const SHOW_DEMO_BLOCK = process.env.NODE_ENV === "development";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Password reset flow
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1=request, 2=confirm
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPw, setResetNewPw] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [devCode, setDevCode] = useState("");

  const openReset = () => {
    setResetEmail(email || "");
    setResetCode(""); setResetNewPw(""); setDevCode(""); setResetStep(1);
    setResetOpen(true);
  };

  const requestReset = async (e) => {
    e?.preventDefault();
    if (!resetEmail || !resetEmail.includes("@")) {
      toast.error(lang === "en" ? "Enter a valid email" : "Ingresa un correo válido");
      return;
    }
    setResetLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: resetEmail });
      if (data.dev_code) setDevCode(data.dev_code);
      toast.success(data.message || (lang === "en" ? "If the email exists, a code was generated" : "Si el correo existe, se generó un código"));
      setResetStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const confirmReset = async (e) => {
    e?.preventDefault();
    if (resetNewPw.length < 8) {
      toast.error(lang === "en" ? "Password must be at least 8 characters" : "La contraseña debe tener al menos 8 caracteres");
      return;
    }
    setResetLoading(true);
    try {
      await api.post("/auth/reset-password", {
        email: resetEmail,
        code: resetCode.trim(),
        new_password: resetNewPw,
      });
      toast.success(lang === "en" ? "Password reset. Please sign in." : "Contraseña restablecida. Inicia sesión.");
      setResetOpen(false);
      setEmail(resetEmail);
      setPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Unified login: any valid role is accepted; we redirect based on role.
      const user = await login(email, password, () => true);
      const role = user?.role;
      const isSupport = role === "agent" || role === "support_admin";
      if (isSupport) {
        toast.success(lang === "en" ? "Welcome to the Support panel" : "Bienvenido al panel Support");
        navigate("/support");
      } else {
        toast.success(lang === "en" ? "Welcome" : "Bienvenido");
        navigate("/");
      }
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const portal = {
    eyebrow: t("login.portal.workspace") || (lang === "en" ? "Workspace" : "Portal"),
    title: t("login.unified.title") || (lang === "en" ? "Sign in" : "Iniciar sesión"),
    subtitle: t("login.unified.subtitle") || (lang === "en" ? "Financial or Support access" : "Acceso Financiero o Soporte"),
    demoEmail: "admin@flexenergy.pro",
    demoPass: "Admin123!",
  };

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 80% 50% at 20% 0%, #122B53 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 80% 100%, #1A1530 0%, transparent 55%), #0A1224",
      }}
    >
      {/* Soft amber accent glow — single, restrained */}
      <div
        className="absolute top-1/2 -translate-y-1/2 -right-32 w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(251,191,36,0.10) 0%, transparent 70%)",
        }}
      />

      {/* Single hairline amber accent at the very bottom — corporate signature */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
        style={{
          background:
            "linear-gradient(to right, transparent 10%, rgba(251,191,36,0.4) 50%, transparent 90%)",
        }}
      />

      {/* Top bar with brand + lang toggle */}
      <header className="relative w-full px-6 lg:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BrandMark size={36} variant="transparent" />
          <div>
            <div className="text-sm font-semibold text-white leading-none tracking-tight">FLEX ENERGY</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80 mt-1">
              Unlimited · Texas
            </div>
          </div>
        </div>
        <ToolbarToggles />
      </header>

      {/* Centered login card — absolutely centered, fits in viewport */}
      <main className="relative flex-1 flex items-center justify-center px-4 py-4">
        <div className="w-full max-w-md">
          <div className="bg-white/[0.96] dark:bg-slate-900/90 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] px-8 sm:px-10 py-8 relative">
            {/* Top hairline amber accent inside card */}
            <div
              className="absolute top-0 left-8 right-8 h-px"
              style={{
                background:
                  "linear-gradient(to right, transparent, rgba(251,191,36,0.55), transparent)",
              }}
            />

            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-600 dark:text-amber-400 font-semibold mb-3 text-center">
              {portal.eyebrow}
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight mb-2 text-center">
              {portal.title}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 text-center">
              {portal.subtitle}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">
                  {t("login.email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                  className="h-11 border-slate-200 dark:border-slate-700 dark:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-500 focus:ring-1 focus:ring-slate-900 dark:focus:ring-amber-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">
                  {t("login.password")}
                </Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  testidEye="login-password-eye"
                  className="h-11 border-slate-200 dark:border-slate-700 dark:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-500 focus:ring-1 focus:ring-slate-900 dark:focus:ring-amber-500"
                />
              </div>
              {error && (
                <div
                  data-testid="login-error"
                  className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-lg px-3 py-2.5"
                >
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-400 dark:text-slate-900 text-white font-semibold rounded-lg transition-colors"
              >
                {loading ? t("login.submitting") : t("login.submit")}
              </Button>
            </form>

            {SHOW_DEMO_BLOCK && (
              <div className="mt-8 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 text-[11px] text-slate-600 dark:text-slate-400">
                <div className="font-semibold text-slate-700 dark:text-slate-300 mb-1">{t("login.demo")} · {portal.eyebrow}</div>
                <div className="font-mono">{portal.demoEmail} / {portal.demoPass}</div>
              </div>
            )}
          </div>

          <div className="mt-6 text-center text-[11px] text-white/40">
            © {new Date().getFullYear()} FLEX ENERGY UNLIMITED · Energy Brokerage Texas
          </div>
        </div>
      </main>
    </div>
  );
}
